import { OnInit, OnDestroy } from '@angular/core';
import { IdentifyingClient } from '../../shared/api/identifying-api.generated';
import { AdministratorClient } from '../../shared/api/administrator-api.generated';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, combineLatest, BehaviorSubject, of } from 'rxjs';
import { filter, switchMap, map, distinctUntilChanged, catchError, debounceTime } from 'rxjs/operators';
import { BsModalService } from 'ngx-bootstrap/modal';
import { UserSettingsService } from '../../core/services/user-settings.service';
import { PortalService } from '../../core/services/portal.service';
import { environment } from '../../../environments/environment';
import { BatchActionService, BatchSelectionType } from '../../core/services/batch-action.service';
import { DeleteModalComponent, DeleteModalProps } from '../../shared/components/delete-modal/delete-modal.component';
import { TalentScansFilterService } from '../talentscans-filter.service';
var TalentScansComponent = /** @class */ (function () {
    function TalentScansComponent(identifyingClient, administratorClient, userSettingsService, portalService, batchActionService, modalService, translate, talentsFilterService) {
        this.identifyingClient = identifyingClient;
        this.administratorClient = administratorClient;
        this.userSettingsService = userSettingsService;
        this.portalService = portalService;
        this.batchActionService = batchActionService;
        this.modalService = modalService;
        this.translate = translate;
        this.talentsFilterService = talentsFilterService;
        this.page = 1;
        this.pageSize = environment.api.pageSize;
        this.talents = [];
        this.subscriptions = [];
        this.fetch$ = new BehaviorSubject(false);
        this.sortOn = '-createdOn';
    }
    TalentScansComponent.prototype.ngOnInit = function () {
        var _this = this;
        var talentsSub = combineLatest(this.fetch$, this.talentsFilterService.filter$, this.userSettingsService.currentAccount$, this.portalService.expectations$, this.portalService.domains$)
            .pipe(filter(function (_a) {
            var fetch = _a[0], talentsFilter = _a[1], account = _a[2], expectations = _a[3], domains = _a[4];
            return fetch && !!account && !!expectations && !!domains;
        }), debounceTime(500), switchMap(function (_a) {
            var fetch = _a[0], talentsFilter = _a[1], account = _a[2], expectations = _a[3], domains = _a[4];
            var offset = (_this.page - 1) * _this.pageSize;
            _this.loading = true;
            return _this.administratorClient.getTalentScansForAccount(account.id, undefined /* externalRef */, undefined /* shouldHaveExpectations */, true /* includeExpectations */, talentsFilter.teamRoleIds, talentsFilter.workValueIds, offset, _this.pageSize, _this.sortOn, talentsFilter.search, talentsFilter.domainIds, undefined /* atsClientId */).pipe(map(function (talents) {
                talents.forEach(function (t) {
                    t.expectations = _this.mapExpectations(t, expectations);
                    t.domains = _this.mapDomains(t, domains);
                });
                return talents;
            }), catchError(function (err) { return of([]); }));
        })).subscribe(function (talents) {
            _this.loading = false;
            Array.prototype.push.apply(_this.talents, talents);
            _this.fetch$.next(false);
        });
        var accountSub = this.userSettingsService
            .currentAccount$
            .pipe(filter(function (p) { return !!p; }), distinctUntilChanged())
            .subscribe(function (account) { return _this.reset(); });
        var batchDeleteSub = this.batchActionService
            .delete$
            .pipe(filter(function (res) { return res.selectionType === BatchSelectionType.Talents; }))
            .subscribe(function (res) { return _this.showBatchDeleteConfirmationModal(res.selection); });
        var batchActionBarVisibleSub = this.batchActionService
            .actionBarVisible$
            .subscribe(function (actionBarVisible) { return _this.actionBarVisible = actionBarVisible; });
        var filterSub = this.talentsFilterService
            .filter$
            .subscribe(function (talentsFilter) {
            _this.filter = talentsFilter;
            _this.reset();
        });
        var totalCountSub = combineLatest(this.userSettingsService.currentAccount$, this.talentsFilterService.filter$)
            .pipe(filter(function (_a) {
            var account = _a[0], talentsFilter = _a[1];
            return !!account;
        }), switchMap(function (_a) {
            var account = _a[0], talentsFilter = _a[1];
            return _this.administratorClient.getTotalNumberOfTalentScansForAccount(account.id, undefined /* shouldHaveExpectations */, talentsFilter.teamRoleIds, talentsFilter.workValueIds, talentsFilter.search, talentsFilter.domainIds);
        })).subscribe(function (totalCount) { return _this.totalCount = totalCount; });
        this.subscriptions.push(talentsSub);
        this.subscriptions.push(accountSub);
        this.subscriptions.push(batchDeleteSub);
        this.subscriptions.push(batchActionBarVisibleSub);
        this.subscriptions.push(filterSub);
        this.subscriptions.push(totalCountSub);
    };
    TalentScansComponent.prototype.ngOnDestroy = function () {
        this.subscriptions.forEach(function (sub) { return sub.unsubscribe(); });
    };
    TalentScansComponent.prototype.getTalents = function () {
        this.page++;
        this.fetch$.next(true);
    };
    TalentScansComponent.prototype.sort = function (sortOn) {
        this.sortOn = sortOn;
        this.reset();
    };
    TalentScansComponent.prototype.onDelete = function (talent) {
        var index = this.talents.indexOf(talent);
        if (index >= 0) {
            this.talents.splice(index, 1);
            this.totalCount--;
        }
    };
    TalentScansComponent.prototype.workValuesChanged = function (workValues) {
        this.filter.workValues = workValues;
        this.talentsFilterService.filter$.next(this.filter);
    };
    TalentScansComponent.prototype.teamRolesChanged = function (teamRoles) {
        this.filter.teamRoles = teamRoles;
        this.talentsFilterService.filter$.next(this.filter);
    };
    TalentScansComponent.prototype.domainsChanged = function (domains) {
        this.filter.domains = domains;
        this.talentsFilterService.filter$.next(this.filter);
    };
    TalentScansComponent.prototype.clearSearch = function () {
        this.filter.search = '';
        this.searchChanged('');
    };
    TalentScansComponent.prototype.searchChanged = function (search) {
        this.talentsFilterService.filter$.next(this.filter);
    };
    TalentScansComponent.prototype.reset = function () {
        this.batchActionService.deSelectAll();
        this.page = 1;
        this.talents.splice(0);
        this.totalCount = 0;
        this.fetch$.next(true);
    };
    TalentScansComponent.prototype.showBatchDeleteConfirmationModal = function (talentIds) {
        var _this = this;
        var count = talentIds.length;
        var header = this.translate.instant('talents.delete_multiple_modal_header', { count: count });
        var message = this.translate.instant('talents.delete_multiple_modal_content', {
            count: count,
            accountName: this.userSettingsService.currentAccount$.getValue().name
        });
        var props = new DeleteModalProps(header, message);
        var initialState = { props: props };
        this.modalService.show(DeleteModalComponent, { initialState: initialState });
        var deleteSub = props.confirm$.subscribe(function () { return _this.deleteTalents(talentIds); });
        this.subscriptions.push(deleteSub);
    };
    TalentScansComponent.prototype.deleteTalents = function (talentIds) {
        var _this = this;
        this.identifyingClient.deleteTalentScans(talentIds.join(','))
            .subscribe(function () {
            _this.batchActionService.deSelectAll();
            _this.batchActionService.actionBarVisible$.next(false);
            talentIds.forEach(function (id) {
                var talent = _this.talents.filter(function (t) { return t.id === id; })[0];
                _this.onDelete(talent);
            });
        });
    };
    TalentScansComponent.prototype.mapExpectations = function (talent, allExpectations) {
        if (!talent.expectations) {
            return [];
        }
        var lang = this.translate.currentLang;
        return talent.expectations.map(function (comp) {
            var translatedExpectation = allExpectations.filter(function (c) { return c.id === comp.expectationId; })[0];
            comp.expectationTypeId = translatedExpectation.expectationTypeId;
            comp.name = translatedExpectation.name[lang];
            comp.iconUrl = translatedExpectation.iconUrl;
            return comp;
        });
    };
    TalentScansComponent.prototype.mapDomains = function (talent, allDomains) {
        if (!talent.domainIds) {
            return [];
        }
        return talent.domainIds.map(function (domainId) { return allDomains.filter(function (dom) { return dom.id === domainId; })[0]; });
    };
    return TalentScansComponent;
}());
export { TalentScansComponent };
